import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { dateAsString } from '../../utils/dates';

// Components
import GatsbyHead from '../../components/layout/gatsby-head';
import Layout, { Container, TwoThirds, SideBar } from '../../components/layout';

import Assemblies from '../../components/assemblies';
import RichText from '../../components/rich-text';
import TableOfContent from '../../components/table-of-contents';
import ContentWithReferences from '../../components/table-of-contents/content-with-references';
import TableOfContentsFootNotes from '../../components/table-of-contents/footnotes';
import LegalSideBar from '../../components/legal-sidebar';
import LinkList from '../../components/link-list';
import PageTitle from '../../components/page-title';
import InlineCallOut from '../../components/inline-callout';
import LegalSubPageList from '../../components/legal-landing-page/subpage-list';
import { createFootnotes } from './rich-text-formatting';
import withSplitTest from '../../components/with-split-test';

// Styles
import { FootNotes } from '../../components/table-of-contents/styles';
import { StyledContentWithSideBar, StyledH1, StyledSearchBox } from './styles';
import {
  ContentWithSideBar,
  Section,
} from '../../components/styled/containers';

// Utils
import { extractLayoutProps } from '../../utils/layout';
import { consistentString } from '../../utils/content-formatting';
import { SkipToContent } from '../../components/header/navigation/styles';

export const Head = ({ data }) => <GatsbyHead pageContent={data.page} />;

const LegalPage = ({ data }) => {
  const { page } = data;

  const {
    title,
    bodyCopy,
    assemblies,
    applicableRegions,
    tableOfContents,
    essentialLinks,
    legislations,
    downloads,
    lastAmended,
    type,
    navigation,
  } = page;

  const pageType = consistentString(type);

  const [modifiedTableOfContents, footnotes] = createFootnotes(tableOfContents);

  const hasBottomSection =
    (footnotes && footnotes.length > 0) ||
    legislations ||
    essentialLinks ||
    downloads;

  const children =
    navigation?.down &&
    navigation.down?.map(({ path, label, page: { pageInformation } }) => ({
      path,
      label,
      description: pageInformation?.shortDescription?.shortDescription,
    }));

  const displaySearchBox = process.env.SHELTER_SITE === 'scotland';

  return (
    <Layout legal {...extractLayoutProps(page, data.contentfulConfig)}>
      <Container>
        <StyledContentWithSideBar>
          <SideBar $left $desktop>
            <SkipToContent href="#article">Skip to article</SkipToContent>
            {displaySearchBox ? (
              <StyledSearchBox
                sectionToSearch="legal"
                placeholder="Search"
                headerText="Search Shelter Legal"
                headingElementType="h2"
              />
            ) : null}
            {navigation?.ancestor ? (
              <LegalSideBar
                hierarchy={navigation.ancestor}
                heading={navigation.ancestor.label}
                id="secondary-nav"
                label="Secondary navigation"
              />
            ) : null}
          </SideBar>
          <TwoThirds>
            <article id="article">
              <PageTitle noContainer>
                <StyledH1>{title}</StyledH1>
              </PageTitle>

              {bodyCopy ? <RichText richText={bodyCopy} /> : null}

              {tableOfContents ? (
                <TableOfContent tableOfContents={tableOfContents} />
              ) : null}

              <InlineCallOut borderColor="red" $insideContainer>
                This content applies to <strong>{applicableRegions}</strong>
              </InlineCallOut>

              <Assemblies assemblies={assemblies} />

              {modifiedTableOfContents ? (
                <ContentWithReferences contents={modifiedTableOfContents} />
              ) : null}

              {lastAmended ? (
                <p>Last updated: {dateAsString(lastAmended, 'D MMMM YYYY')}</p>
              ) : null}

              {pageType === 'landing' && children && children.length > 0 ? (
                <LegalSubPageList items={children} />
              ) : null}

              <div id="hotjar-embedded-survey-original" />
            </article>
          </TwoThirds>
        </StyledContentWithSideBar>
      </Container>

      {hasBottomSection ? (
        <Section $offWhite>
          {footnotes && footnotes.length > 0 ? (
            <FootNotes>
              <Container>
                <ContentWithSideBar>
                  <TwoThirds $right>
                    <TableOfContentsFootNotes referenceList={footnotes} />
                  </TwoThirds>
                </ContentWithSideBar>
              </Container>
            </FootNotes>
          ) : null}
          {legislations ? (
            <Container>
              <ContentWithSideBar>
                <TwoThirds right>
                  <LinkList
                    insideContainer
                    links={legislations}
                    headerText="Legislation"
                  />
                </TwoThirds>
              </ContentWithSideBar>
            </Container>
          ) : null}
          {essentialLinks ? (
            <Container>
              <ContentWithSideBar>
                <TwoThirds right>
                  <LinkList
                    insideContainer
                    links={essentialLinks}
                    headerText="Essential Links"
                  />
                </TwoThirds>
              </ContentWithSideBar>
            </Container>
          ) : null}
          {downloads ? (
            <Container>
              <ContentWithSideBar>
                <TwoThirds right>
                  <LinkList
                    downloads
                    insideContainer
                    links={downloads.files}
                    headerText="Downloads"
                  />
                </TwoThirds>
              </ContentWithSideBar>
            </Container>
          ) : null}
        </Section>
      ) : null}
    </Layout>
  );
};
LegalPage.propTypes = {
  data: PropTypes.shape({
    applicableRegions: PropTypes.string,
    content: PropTypes.object,
    page: PropTypes.object,
    contentfulConfig: PropTypes.object,
  }),
  pageContext: PropTypes.object,
};

export default withSplitTest(LegalPage);

export const legalPageQuery = graphql`
  query legalPageQuery($contentful_id: String!) {
    page: contentfulPageLegal(contentful_id: { eq: $contentful_id }) {
      ...PageLegalFragment
      downloads {
        files {
          ...DownloadableFileFragment
        }
      }
    }
    ...NavigationFragment
  }
`;
